<template>
  <BaseBlockLayout @on-close="handleOnClose" @on-open="emit('on-open')">
    <template #title>
      {{ i18n(translations.title) }}
    </template>

    <template #subtitle>
      {{ i18n(translations.subtitle) }}
    </template>

    <template #info v-if="hasOpportunities">
      {{
        i18n(translations.info, {
          count: metric.count,
          percentage: metric.threshold,
        })
      }}
    </template>

    <template #sub-info>
      {{ i18n(translations.saveTime) }}
    </template>

    <template #success v-if="!hasOpportunities">
      {{ i18n(translations.success) }}
    </template>

    <template #content v-if="hasOpportunities">
      <Filters
        ref="filters"
        class="mb-24 px-24"
        @submit="handleFiltersSubmit"
      />

      <div v-if="errorOnUpdate" class="mb-7 px-12 py-8">
        <div class="bg-static-danger-mid rounded-lg px-12 py-8">
          {{ errorOnUpdate }}
        </div>
      </div>

      <div v-if="metadata.canIncreasePageSize" class="flex justify-end px-24">
        <RevInputSelect
          id="pageSize"
          v-model="pageSize"
          class="w-full lg:w-[12rem]"
          :label="i18n(baseBlockTranslations.pageSize)"
          :options="PAGE_SIZE_OPTIONS"
          @update:model-value="handlePageSizeChanged"
        />
      </div>

      <template v-if="!pending">
        <OpportunityTable
          v-if="opportunityTable.length > 0"
          :amplitude-opportunity-type-name="props.amplitudeOpportunityTypeName"
          :caption="
            i18n(translations.items, { count: totalOpportunitiesCount })
          "
          :opportunities="opportunityTable"
          :should-show-backbox-price="props.shouldShowBackboxPrice"
          @on-update="handleOnUpdate"
          @sort-options-changed="handleSortOptionChanged"
        />

        <NoResults v-else />
      </template>

      <RevLoadingScreen
        v-if="pending"
        class="py-72"
        :text="i18n(translations.fetchingData)"
        variant="light"
      />

      <Pagination
        class="flex justify-center py-56"
        :current-page
        :page-count
        @click="handlePaginationClick"
      />
    </template>
  </BaseBlockLayout>
</template>

<script setup lang="ts">
import { navigateTo, useRoute } from '#imports'
import { computed, ref } from 'vue'

import { HttpApiError, sellerOpportunities } from '@backmarket/http-api'
import { useHttpFetch } from '@backmarket/nuxt-module-http/useHttpFetch'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useTheToast } from '@backmarket/nuxt-module-toast/useTheToast'
import { RevInputSelect } from '@ds/components/InputSelect'
import { RevLoadingScreen } from '@ds/components/LoadingScreen'
import type { SortOptions } from '@ds/components/Table'

import { ROUTE_NAMES } from '~/constants/routes'

import { useMetadata } from '../../composables/useMetadata'
import type { FilterValues } from '../Filters/Filters.types'
import Filters from '../Filters/Filters.vue'
import NoResults from '../NoResults/NoResults.vue'
import OpportunityTable from '../OpportunityTable/OpportunityTable.vue'

import {
  DEFAULT_CURRENT_PAGE,
  DEFAULT_PAGE_SIZE,
  PAGE_SIZES,
  PAGE_SIZE_OPTIONS,
} from './BaseBlock.constants'
import baseBlockTranslations from './BaseBlock.translations'
import type { Translations } from './BaseBlock.types'
import BaseBlockLayout from './components/BaseBlockLayout/BaseBlockLayout.vue'

const props = withDefaults(
  defineProps<{
    amplitudeOpportunityTypeName: string
    metric: sellerOpportunities.PricingOpportunitiesMetric
    shouldShowBackboxPrice?: boolean
    translations: Translations
  }>(),
  {
    shouldShowBackboxPrice: true,
  },
)

const emit = defineEmits(['on-pagination-changed', 'on-open', 'on-update'])

const route = useRoute()
const i18n = useI18n()
const { metadata } = useMetadata()
const { openErrorToast } = useTheToast()

const errorOnUpdate = ref<string | null>(null)

const hasOpportunities = computed(
  () => props.metric?.count && props.metric.count > 0,
)

const queryParams = computed(() => ({
  state: props.metric.code,
  ...route.query,
}))

const {
  data: opportunities,
  pending,
  error,
} = useHttpFetch(sellerOpportunities.getPricingOpportunities, {
  queryParams,
})

if (error.value) {
  const { title: content } = HttpApiError.fromAnyError(error.value)
  openErrorToast({ content })
}

const opportunityTable = computed(() => opportunities.value?.results || [])

const totalOpportunitiesCount = computed(() => opportunities.value?.count || 0)

const pageSize = ref(
  route.query.pageSize && PAGE_SIZES.includes(route.query.pageSize.toString())
    ? route.query.pageSize.toString()
    : DEFAULT_PAGE_SIZE,
)

const currentPage = computed(
  () =>
    parseInt(route.query.page?.toString() || '', 10) || DEFAULT_CURRENT_PAGE,
)

const pageCount = computed(() =>
  Math.ceil(totalOpportunitiesCount.value / parseInt(pageSize.value, 10)),
)

async function handleSortOptionChanged(sortOptions: SortOptions) {
  const { sortBy, sortOrder } = sortOptions

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { orderBy, ...newQuery } = route.query

  if (sortOrder !== 'UNDEFINED') {
    newQuery.orderBy = `${sortBy}:${sortOrder.toLowerCase()}`
  }

  await navigateTo({
    ...route,
    query: newQuery,
    replace: true,
  })
}

async function handleOnClose() {
  await navigateTo({ name: ROUTE_NAMES.OPPORTUNITIES.PRICING, replace: true })
}

async function handleFiltersSubmit(values: FilterValues) {
  await navigateTo({ ...route, query: { ...values }, replace: true })
}

async function handlePaginationClick({ page }: { page: number }) {
  await navigateTo({ ...route, query: { ...route.query, page }, replace: true })
  emit('on-pagination-changed', page)
}

async function handlePageSizeChanged() {
  await navigateTo({
    ...route,
    query: {
      ...route.query,
      page: DEFAULT_CURRENT_PAGE,
      pageSize: pageSize.value,
    },
    replace: true,
  })
}

function handleOnUpdate(updateMessages?: { errorMessage: string }) {
  errorOnUpdate.value = updateMessages?.errorMessage || null

  emit('on-update')
}
</script>
